import React, { Fragment, useState, useEffect, Key, useRef, useCallback } from 'react';
import { HiChevronRight, HiChevronDown, HiChevronDoubleLeft, HiChevronDoubleRight } from 'react-icons/hi';
import { NavLink } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import { NavBarSecondaryMenu } from './navbarData';
import Layers from 'components/Layers';
import Legend from 'containers/Legend';
import { RgbaColor } from "react-colorful";
import { SymbologyModelRecord } from 'models/SymbologyModel';
import { SymbologyModel } from 'models/SymbologyModel';
import { LayerAttributeStyle } from 'models/LayerAttributeStyle';
import { LayerRuleStyleRecord } from 'models/LayerRuleStyle';
import './style.scss';
import { FiRefreshCw } from 'react-icons/fi';
import { getLegendStructure } from "../../containers/Legend/getLegendStructure";
import { useLegendActions } from "actions/legend";
import type { LayerExportProgress } from 'utils/layerExportProgress';
import type { ContextMenuLayerNode } from 'types/layerRefresh';
import SurveyManager from 'containers/SurveyManager';
import { loadSurveyManagerCatalogue } from "../../containers/SurveyManager/loadSurveyManagerCatalogue";
import { useSurveyManagerActions } from "actions/surveymanager";
import { safeLocalStorageSetItem } from 'utils/safeLocalStorage';
import { useAppSelector } from 'hooks/appSelector';
import { selectActiveDefectLayers } from 'state/surveyDataManager';
import { mergeScannerMockCatalogue } from 'domain/surveyDataManager/api/mergeScannerMockCatalogue';
import { mergeDefectOverlayCatalogDefects } from 'domain/surveyDataManager/defectOverlays/mergeDefectOverlayCatalogs';
import { useAvailableViews, useDefects } from 'domain/surveyDataManager/hooks';
import {
  FAVOURITES_CHANGED_EVENT,
  getFavouriteBaseKeys,
  loadFavouritesMap,
  normalizeFavouriteBaseKey,
} from 'utils/favouritesStorage';
import { syncSdmFavouriteCheckedKeys } from 'containers/SurveyDataManager/components/AvailableViewsPanel/sdmFavouriteKeys';
import { collectActiveRecipeIds } from 'containers/SurveyDataManager/recipeVisibility';
import { isSurveyCoverageFavouriteNode } from 'utils/layerEndPointUrl';
import { syncSurveyCoverageFavouriteCheckedKeys } from 'utils/surveyCoverageFavouriteSync';


interface Props {
  onAction: (title: string, refreshLegendToolbar?: boolean) => void,
  activeActions: Record<string, boolean>,
  isExpanded: boolean | null,
  setIsExpanded: (value: boolean) => void,
  currentLayer: string,
  onLayerChange: (title: string) => void,
  onNodeChecked: (checkedNodes: Array<any>, currentSelectedNode?: any) => void,
  onNodeCheckedSurveyManager: (checkedNodes: Array<any>, currentSelectedNode?: any) => void,
  onSurveyLayerTypesLoaded: (layerTypes: string[]) => void,
  setLayerStyle: (key: string, color: RgbaColor, width: number, size: number, lineStyle: string, markerStyle: string, angle: number) => void,
  setLayerVisualisationRules: (key: string, type: string, style: SymbologyModel, layerAttributeStyles: LayerAttributeStyle[]) => void,
  zoomToSection: (key: string) => void,
  bringLayerToTop: (layerKey: string) => void,
  layersStyle: SymbologyModelRecord,
  layerRulesStyles: LayerRuleStyleRecord,
  zoomStackIsAllowed: boolean,
  emapsiteBasemapsEnabled: boolean,
  layerRefreshLoading: Record<string, { isLoading: boolean; hasError: boolean; errorMessage?: string }>,
  setLayerRefreshLoading: React.Dispatch<React.SetStateAction<Record<string, { isLoading: boolean; hasError: boolean; errorMessage?: string }>>>,
  checkedNodesState: any[],
  setCheckedNodesState: (nodes: any[]) => void,
  handleContextMenuRefreshClicked: (
    selectedKeys: Key[],
    contextNode?: ContextMenuLayerNode | null,
  ) => void,
  rightSidebarRef: React.RefObject<HTMLDivElement>,
  showDirection: boolean,
  setShowDirection: (value: boolean) => void,
  exportToShapefile: (targetLayerNode?: any, legendTreeData?: readonly any[]) => Promise<void>,
  cancelShapefileExport: () => void,
  isExporting: boolean,
  exportProgress?: LayerExportProgress | null,
  layerData: Record<string, any>,
}

const ToolBar: React.FC<Props> = ({
  onAction,
  activeActions,
  isExpanded,
  setIsExpanded,
  currentLayer,
  onLayerChange,
  onNodeChecked,
  onNodeCheckedSurveyManager,
  onSurveyLayerTypesLoaded,
  setLayerStyle,
  setLayerVisualisationRules,
  bringLayerToTop,
  zoomToSection,
  layersStyle = {},
  layerRulesStyles = {},
  zoomStackIsAllowed,
  emapsiteBasemapsEnabled,
  setCheckedNodesState,
  handleContextMenuRefreshClicked,
  checkedNodesState,
  setLayerRefreshLoading,
  layerRefreshLoading,
  rightSidebarRef,
  showDirection,
  setShowDirection,
  exportToShapefile,
  cancelShapefileExport,
  isExporting,
  exportProgress,
  layerData,
}) => {

  const [isSubmenuOpened, setSubmenuOpened] = useState({} as { [k: string]: any });
  const [mountedSubmenus, setMountedSubmenus] = useState<Record<string, boolean>>(
    {},
  );
  const legendActions = useLegendActions();
  const surveyManagerActions = useSurveyManagerActions();
  const [toolbarRefreshLoading, setToolbarRefreshLoading] = useState(false);
  const [toolbarRefreshLoadingSurveyManager, setToolbarRefreshLoadingSurveyManager] = useState(false);
  const [treeData, setTreeData] = useState<any[]>([]);
  const [treeDataSurveyManager, setTreeDataSurveyManager] = useState<any[]>([]);
  // Lifted so checkbox state survives Legend remount when the toolbar collapses
  const [surveyManagerCheckedKeys, setSurveyManagerCheckedKeys] = useState<Key[]>([]);
  const [legendCheckedKeys, setLegendCheckedKeys] = useState<Key[]>(['network-layer']);
  const [favouritesEpoch, setFavouritesEpoch] = useState(0);
  const activeDefectLayers = useAppSelector(selectActiveDefectLayers);
  const defectsQuery = useDefects({}, false);
  const availableViewsQuery = useAvailableViews(false);
  const catalogDefects = mergeDefectOverlayCatalogDefects(
    mergeScannerMockCatalogue(defectsQuery.data?.defects ?? []),
  );
  const availableViews = availableViewsQuery.data?.views ?? [];
  const treeDataRef = useRef(treeData);
  const treeDataSurveyManagerRef = useRef(treeDataSurveyManager);
  treeDataRef.current = treeData;
  treeDataSurveyManagerRef.current = treeDataSurveyManager;

  useEffect(() => {
    const onFavouritesChanged = () => {
      setFavouritesEpoch((epoch) => epoch + 1);
    };
    window.addEventListener(FAVOURITES_CHANGED_EVENT, onFavouritesChanged);
    return () =>
      window.removeEventListener(FAVOURITES_CHANGED_EVENT, onFavouritesChanged);
  }, []);

  useEffect(() => {
    const favourites = loadFavouritesMap();
    const favouriteKeys = [...favourites.keys()].map(normalizeFavouriteBaseKey);
    const activeRecipeIds = collectActiveRecipeIds(
      activeDefectLayers,
      availableViews,
      catalogDefects,
    );
    const surveyCoverageActive = new Set(
      surveyManagerCheckedKeys.map((key) => normalizeFavouriteBaseKey(String(key))),
    );

    setLegendCheckedKeys((previous) => {
      let synced = syncSdmFavouriteCheckedKeys(
        previous.map(String),
        favouriteKeys,
        activeRecipeIds,
      );

      const favouriteBaseKeys = getFavouriteBaseKeys(favourites);
      for (const baseKey of favouriteBaseKeys) {
        const favouriteNode =
          favourites.get(`favourites_${baseKey}`) ?? favourites.get(baseKey);
        if (!favouriteNode || !isSurveyCoverageFavouriteNode(favouriteNode)) {
          continue;
        }
        synced = syncSurveyCoverageFavouriteCheckedKeys(
          synced,
          baseKey,
          surveyCoverageActive.has(baseKey),
        );
      }

      const previousSet = new Set(previous.map(String));
      if (
        previousSet.size === synced.length &&
        synced.every((key) => previousSet.has(key))
      ) {
        return previous;
      }
      return synced;
    });
  }, [
    activeDefectLayers,
    availableViews,
    catalogDefects,
    favouritesEpoch,
    surveyManagerCheckedKeys,
  ]);

  const syncSurveyManagerFromCoverageFavourite = useCallback(
    (layerKey: string, checked: boolean) => {
      const baseKey = normalizeFavouriteBaseKey(layerKey);
      setSurveyManagerCheckedKeys((previous) => {
        const next = new Set(previous.map(String));
        if (checked) {
          next.add(baseKey);
        } else {
          next.delete(baseKey);
        }
        const asArray = [...next];
        if (
          asArray.length === previous.length &&
          asArray.every((key, index) => String(previous[index]) === key)
        ) {
          return previous;
        }
        return asArray;
      });
    },
    [],
  );
  useEffect(() => {
    const navbarExpanded = localStorage.getItem('toolbarExpanded');
    setIsExpanded(navbarExpanded === 'true');
    localStorage.removeItem('submenuState');
  }, []);

  useEffect(() => {
    if (isExpanded !== null) {
      localStorage.setItem('toolbarExpanded', isExpanded.toString());
    }
  }, [isExpanded]);

  const toggleSubmenu = (itemName: string) => {
    setMountedSubmenus((mounted) =>
      mounted[itemName] ? mounted : { ...mounted, [itemName]: true },
    );
    setSubmenuOpened((sub) => {
      if (!isExpanded) {
        return {
          ...sub,
          [itemName]: true,
        };
      }
      return {
        ...sub,
        [itemName]: sub[itemName] === undefined ? true : !sub[itemName],
      };
    });
  };

  const toggleNavBar = () => {
    setIsExpanded(!isExpanded);
  };

  const handleActionClick = (event: React.MouseEvent<HTMLElement>, title: string) => {
    event.preventDefault();
    if (title === "Legend" || title === "Basemaps" || title === "Survey Image Viewer") {
      setIsExpanded(true);
    }
    onAction(title);
  };

  const handleRefresh = async (e: React.MouseEvent<SVGElement, MouseEvent>, title: string) => {
    setToolbarRefreshLoading(true);
    e.stopPropagation();
    e.preventDefault();
    await refreshLegendStructure(true);
    setToolbarRefreshLoading(false);
  };

  const handleRefreshSurveyManager = async (e: React.MouseEvent<SVGElement, MouseEvent>, title: string) => {

    setToolbarRefreshLoadingSurveyManager(true);
    e.stopPropagation();
    e.preventDefault();
    await refreshSurveyManagerStructure(true);
    setToolbarRefreshLoadingSurveyManager(false);
  };

  const refreshLegendStructure = async (forceRefresh: boolean) => {
    try {
      const legendResponse = await legendActions.getLegend(forceRefresh);
      if (!legendResponse) {
        throw new Error("No legend response received");
      }
      const trData = getLegendStructure(legendResponse);
      const currentTenant = localStorage.getItem('tenantId');
      const currentUser = JSON.parse(localStorage.getItem('userData') || '{}') as { username?: string };
      const savedTreeDataKey = `treeData_${currentTenant}_${currentUser?.username}`;
      const savedTreeData = localStorage.getItem(savedTreeDataKey);

      if (savedTreeData) {
        try {
          const parsedTreeData = JSON.parse(savedTreeData);
          const normalizedTreeData = Array.isArray(parsedTreeData) ? parsedTreeData : [parsedTreeData];

          // Only carry Favourites across refreshes. Merging the full saved tree
          // into the freshly fetched legend duplicates nodes on every refresh
          // until localStorage hits its quota.
          const favouriteNodeIndex = normalizedTreeData.findIndex(node => node.title === "Favourites" || node.key === "Favourites");
          let favouriteNode = null;
          if (favouriteNodeIndex !== -1) {
            favouriteNode = normalizedTreeData[favouriteNodeIndex];
          } else {
            const existingFavouriteIndex = treeData.findIndex(node => node.title === "Favourites" || node.key === "Favourites");
            if (existingFavouriteIndex !== -1) {
              favouriteNode = treeData[existingFavouriteIndex];
            }
          }

          if (favouriteNode) {
            trData.unshift(favouriteNode);
          }
        } catch (parseError) {
          console.error("Failed to parse saved tree data:", parseError);
        }
      } else {
        const existingFavouriteIndex = treeData.findIndex(node => node.title === "Favourites" || node.key === "Favourites");
        if (existingFavouriteIndex !== -1) {
          const [favouriteNode] = treeData.splice(existingFavouriteIndex, 1);
          trData.unshift(favouriteNode);
        }
      }

      setTreeData(trData);
      safeLocalStorageSetItem(savedTreeDataKey, JSON.stringify(trData));
    } catch (err) {
      console.error("Error refreshing legend:", err);
    } finally {
      setToolbarRefreshLoading(false);
    }
  };

  const refreshSurveyManagerStructure = async (forceRefresh: boolean) => {
    try {
      await loadSurveyManagerCatalogue(
        {
          getLayers: surveyManagerActions.getLayers,
          getSurveyShapefiles: surveyManagerActions.getSurveyShapefiles,
        },
        {
          onLayerTypesLoaded: (layerTypes) => {
            onSurveyLayerTypesLoaded([...layerTypes]);
          },
          onTreeUpdated: setTreeDataSurveyManager,
        },
        { forceRefresh },
      );
    } catch (err) {
      console.error("Error refreshing survey manager:", err);
      if (forceRefresh) {
        setTreeDataSurveyManager([]);
        onSurveyLayerTypesLoaded([]);
      }
    } finally {
      setToolbarRefreshLoadingSurveyManager(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const prefetchNavigationCatalogues = async () => {
      const tasks: Promise<void>[] = [];

      if (treeDataRef.current.length === 0) {
        tasks.push(
          refreshLegendStructure(false).catch((err: unknown) => {
            console.error('Failed to prefetch legend catalogue:', err);
          }),
        );
      }

      if (treeDataSurveyManagerRef.current.length === 0) {
        tasks.push(
          refreshSurveyManagerStructure(false).catch((err: unknown) => {
            console.error('Failed to prefetch survey image viewer catalogue:', err);
          }),
        );
      }

      await Promise.all(tasks);
      if (cancelled) {
        return;
      }
    };

    void prefetchNavigationCatalogues();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className={`xa-toolbar${isExpanded ? ' expanded' : ' collapsed'}`} ref={rightSidebarRef}>
      {NavBarSecondaryMenu.map((item) => (
        <Fragment key={item.title}>
          <div
            className={`xa-toolbar-item${(item.title === "Basemaps" || item.title === "Legend" || item.title === "Survey Image Viewer") ? (isSubmenuOpened[item.title] ? ' submenu-opened' : '') : ''}`}
            data-tip={item.title}
            data-for={item.title}
          >
            <NavLink
              to={item.path}
              className={(item.title === "Basemaps" || item.title === "Legend" || item.title === "Survey Image Viewer") ? '' : (() => activeActions[item.title] ? 'active' : '')}
              onClick={(e) => {
                handleActionClick(e, item.title);
                if (
                  item.title === 'Basemaps' ||
                  item.title === 'Legend' ||
                  item.title === 'Survey Image Viewer'
                ) {
                  toggleSubmenu(item.title);
                }
              }}
            >
              <span className="xa-toolbar-item-icon">{item.icon}</span>
              <span className="xa-toolbar-item-main">
                <span className="xa-toolbar-item-text">{item.title}</span>
                {item.title === 'Legend' && isSubmenuOpened.Legend && (
                  <FiRefreshCw
                    onClick={e => handleRefresh(e, item.title)}
                    className={`xa-toolbar-item-refresh${toolbarRefreshLoading ? ' rotating' : ''}`}
                  />
                )}
                {item.title === "Survey Image Viewer" && isSubmenuOpened[item.title] && (
                  <FiRefreshCw
                    onClick={e => handleRefreshSurveyManager(e, item.title)}
                    className={`xa-toolbar-item-refresh${toolbarRefreshLoadingSurveyManager ? ' rotating' : ''}`}
                  />
                )}
              </span>
              {(item.title === "Basemaps" || item.title === "Legend" || item.title === "Survey Image Viewer") && (
                <span className="xa-toolbar-item-parent-icon">
                  {isSubmenuOpened[item.title] ? <HiChevronDown /> : <HiChevronRight />}
                </span>
              )}
            </NavLink>
            <div
              className={`xa-toolbar-submenu${
                item.title === 'Basemaps' ||
                item.title === 'Legend' ||
                item.title === 'Survey Image Viewer'
                  ? ' xa-toolbar-submenu--collapsible'
                  : ''
              }`}
            >
              <div className="xa-toolbar-submenu-inner">
              {item.title === "Basemaps" && mountedSubmenus.Basemaps && (
                <Layers
                  currentLayer={currentLayer}
                  onLayerChange={onLayerChange}
                  zoomStackIsAllowed={zoomStackIsAllowed}
                  emapsiteBasemapsEnabled={emapsiteBasemapsEnabled}
                />
              )}
              {item.title === "Legend" && mountedSubmenus.Legend && (
                <Legend
                  onNodeChecked={onNodeChecked}
                  onNodeCheckedSurveyManager={onNodeCheckedSurveyManager}
                  onSurveyCoverageFavouriteSync={syncSurveyManagerFromCoverageFavourite}
                  setLayerStyle={setLayerStyle}
                  setLayerVisualisationRules={setLayerVisualisationRules}
                  bringLayerToTop={bringLayerToTop}
                  zoomToSection={zoomToSection}
                  styles={layersStyle}
                  layerRulesStyles={layerRulesStyles}
                  setCheckedNodesState={setCheckedNodesState}
                  handleContextMenuRefreshClicked={handleContextMenuRefreshClicked}
                  setTreeData={setTreeData}
                  treeData={treeData}
                  checkedNodesState={checkedNodesState}
                  setLayerRefreshLoading={setLayerRefreshLoading}
                  layerRefreshLoading={layerRefreshLoading}
                  showDirection={showDirection}
                  setShowDirection={setShowDirection}
                  exportToShapefile={exportToShapefile}
                  cancelShapefileExport={cancelShapefileExport}
                  isExporting={isExporting}
                  exportProgress={exportProgress}
                  layerData={layerData}
                  checkedKeys={legendCheckedKeys}
                  setCheckedKeys={setLegendCheckedKeys}
                />
              )}
              {item.title === "Survey Image Viewer" &&
                mountedSubmenus['Survey Image Viewer'] && (
                <SurveyManager
                  onNodeCheckedSurveyManager={onNodeCheckedSurveyManager}
                  onSurveyLayerTypesLoaded={onSurveyLayerTypesLoaded}
                  setTreeDataSurveyManager={setTreeDataSurveyManager}
                  treeDataSurveyManager={treeDataSurveyManager}
                  setCheckedNodesState={setCheckedNodesState}
                  checkedNodesState={checkedNodesState}
                  checkedKeys={surveyManagerCheckedKeys}
                  setCheckedKeys={setSurveyManagerCheckedKeys}
                  layerRefreshLoading={layerRefreshLoading}
                  handleContextMenuRefreshClicked={handleContextMenuRefreshClicked}
                  styles={layersStyle}
                  setLayerStyle={setLayerStyle}
                  bringLayerToTop={bringLayerToTop}
                  zoomToSection={zoomToSection}
                />
              )}
              </div>
            </div>
          </div>
          {!isExpanded && (
            <ReactTooltip
              id={item.title}
              place="left"
              type="dark"
              effect="solid"
              backgroundColor="var(--xa-black)"
              textColor="var(--xa-white)"
            />
          )}
        </Fragment>
      ))}
      <div className="xa-toolbar-toggle" onClick={toggleNavBar}>
        <span className="xa-toolbar-toggle-icon">
          {isExpanded ? <HiChevronDoubleRight /> : <HiChevronDoubleLeft />}
        </span>
        <span className="xa-toolbar-toggle-text">Collapse</span>
      </div>
    </div>
  );
};

export default ToolBar;